import { format } from 'date-fns';
import { isMembershipLine, type MembershipLineShape } from '@notifi/shared-types/membership-line';
import { dateStringToDate } from '@/lib/date-values';
import { READY_TO_DISPATCH_LABEL } from '@/components/organisms/jobs/support/visit-status-copy';
import { hasJobLineItems } from '@/lib/jobs/jobScopeValidation';
import type { Invoice } from '@/types/invoice';
import type { InvoicingSchedule, Job, RecurringStatus } from '@/types/job';
import type { JobAppointment, JobAppointmentStatus } from '@/types/jobAppointment';

/**
 * Format a date-only (@db.Date) field at local midnight — never the instant
 * formatter, which would shift it a day in negative-offset zones.
 */
export function dateOnly(iso: string | null | undefined, pattern = 'MMM d, yyyy'): string {
  const d = dateStringToDate(iso ?? undefined);
  return d ? format(d, pattern) : '—';
}

/** ISO weekday (1 = Monday … 7 = Sunday) → US-English day name. Matches the
 *  routeMembership / InsertionCandidate weekday encoding. */
export const ISO_WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export function isoWeekdayName(weekday: number): string {
  return ISO_WEEKDAY_NAMES[weekday - 1] ?? `Day ${weekday}`;
}

/** USD money; whole-dollar by default (mockup shows `$185`, `$1,343`). */
export function money(value: number | null | undefined, cents = false): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
}

export type PillTone = 'ok' | 'warn' | 'info' | 'muted' | 'danger';

export const PILL_TONE_CLASS: Record<PillTone, string> = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  muted: 'bg-muted text-muted-foreground border-border',
  danger: 'bg-destructive/10 text-destructive border-destructive/30',
};

/** Series status pill: payment recovery states are distinct from manual pause. */
export function seriesStatusPill(status: RecurringStatus): {
  label: string;
  tone: PillTone;
} {
  switch (status) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'ok' };
    case 'PAST_DUE':
      return { label: 'Past due', tone: 'warn' };
    case 'SUSPENDED':
      return { label: 'Suspended', tone: 'danger' };
    case 'PAUSED':
      return { label: 'Paused', tone: 'warn' };
    case 'ENDED':
      return { label: 'Ended', tone: 'muted' };
    default:
      return { label: 'Draft', tone: 'muted' };
  }
}

// Actions that require the series to generate or dispatch visits. Past due stays
// workable during the grace period; suspended work and ended series do not.
export function canScheduleVisit(status: RecurringStatus): boolean {
  return status !== 'ENDED' && status !== 'SUSPENDED';
}

export function canAssignRoute(status: RecurringStatus): boolean {
  return status !== 'ENDED' && status !== 'SUSPENDED';
}

export function canUseRecurringTimeEntryAction(
  status: RecurringStatus,
  hasAppointment: boolean,
): boolean {
  return hasAppointment || canScheduleVisit(status);
}

/** Visit status pill: complete mapping so rows show the real appointment state. */
export function visitStatusPill(status: JobAppointmentStatus): {
  label: string;
  tone: PillTone;
} {
  switch (status) {
    case 'RESERVED':
      return { label: 'Reserved', tone: 'muted' };
    case 'SCHEDULED':
      return { label: 'Scheduled', tone: 'muted' };
    case 'CONFIRMED':
      return { label: READY_TO_DISPATCH_LABEL, tone: 'ok' };
    case 'DISPATCHED':
      return { label: 'Dispatched', tone: 'info' };
    case 'IN_PROGRESS':
      return { label: 'In progress', tone: 'info' };
    case 'ON_HOLD':
      return { label: 'On hold', tone: 'warn' };
    case 'COMPLETED':
      return { label: 'Completed', tone: 'ok' };
    case 'CANCELLATION_PENDING':
      return { label: 'Cancellation pending', tone: 'warn' };
    case 'SKIPPED':
      return { label: 'Skipped', tone: 'warn' };
    case 'CANCELLED':
    case 'EXPIRED':
      return { label: 'Cancelled', tone: 'muted' };
    default:
      return { label: 'Scheduled', tone: 'muted' };
  }
}

/** Terminal (billed/reason) vs upcoming, for panel layout selection. */
export function isCompletedVisit(a: JobAppointment): boolean {
  return a.status === 'COMPLETED';
}
export function isSkippedVisit(a: JobAppointment): boolean {
  return a.status === 'SKIPPED' || a.status === 'CANCELLED' || a.status === 'EXPIRED';
}

/**
 * Mode-only billing inputs, decoupled from the `Job` shape so the create form
 * (which holds the raw form fields) and the detail view (which holds a `Job`)
 * can share the same headline/summary copy. Fill from either source.
 */
export interface BillingModeInput {
  billingMode?: 'PER_VISIT' | 'SUBSCRIPTION' | 'PREPAID' | null;
  baseAmount?: number | null;
  cadenceFreq?: 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY' | null;
  invoicingSchedule?: InvoicingSchedule | null;
}

/**
 * Billing headline core: per-visit `$X /visit`, subscription `$X /mo`
 * (`/qtr`, `/yr` by cadence), prepaid `$X term`.
 */
export function billingHeadlineFrom({ billingMode, baseAmount, cadenceFreq }: BillingModeInput): {
  amount: number | null;
  unit: string;
  caption: string;
} {
  const amount = baseAmount ?? null;
  switch (billingMode) {
    case 'SUBSCRIPTION': {
      const unit =
        cadenceFreq === 'ANNUALLY' ? '/yr' : cadenceFreq === 'QUARTERLY' ? '/qtr' : '/mo';
      return { amount, unit, caption: 'Subscription billing' };
    }
    case 'PREPAID':
      return { amount, unit: ' term', caption: 'Prepaid term' };
    case 'PER_VISIT':
    default:
      return { amount, unit: '/visit', caption: 'Per-visit billing' };
  }
}

/** What the membership lines add to a job's total. */
function membershipSaleTotal(lines: Job['lineItems'] | undefined): number {
  let total = 0;
  for (const line of lines ?? []) {
    if (!isMembershipLine(line as MembershipLineShape)) continue;
    const explicit = Number(line['lineTotal'] ?? line['lineSubtotal'] ?? NaN);
    if (Number.isFinite(explicit)) {
      total += explicit;
      continue;
    }
    const qty = Number(line['quantity'] ?? 1);
    const unit = Number(line['basePrice'] ?? line['unitPrice'] ?? line['price'] ?? 0);
    if (Number.isFinite(qty) && Number.isFinite(unit)) total += qty * unit;
  }
  return total;
}

/**
 * Billing headline from the plan's billing mode + base amount:
 * per-visit `$X /visit`, subscription `$X /mo`, prepaid `$X term`.
 */
export function billingHeadline(job: Job): {
  amount: number | null;
  unit: string;
  caption: string;
} {
  const cfg = job.recurringConfig ?? null;
  const billingMode = cfg?.billingMode ?? null;
  // A per-visit plan priced by its services has no base amount of its own:
  // each visit bills the job's scope, the total the job header shows, less a
  // membership the series sells once — that sale rides one invoice, not
  // every visit. A plan whose only line is that membership has no visit
  // price at all, as the customer's pages say.
  const visitLines = (job.lineItems ?? []).filter(
    (line) => !isMembershipLine(line as MembershipLineShape),
  );
  const perVisitScope =
    (billingMode ?? 'PER_VISIT') === 'PER_VISIT' &&
    hasJobLineItems(job) &&
    visitLines.length > 0 &&
    job.grandTotal != null
      ? Math.round((job.grandTotal - membershipSaleTotal(job.lineItems)) * 100) / 100
      : null;
  return billingHeadlineFrom({
    billingMode,
    baseAmount: cfg?.baseAmount ?? perVisitScope,
    cadenceFreq: cfg?.billingCadence?.freq ?? null,
  });
}

/** How long after a visit completes its own invoice is still expected. */
export const VISIT_INVOICE_WAIT_MS = 2 * 60_000;

/**
 * The Billing tab's "Scheduled revenue" row, said the way each model bills. An
 * ongoing subscription has no total to project — it bills its fee every month —
 * so it shows the fee, not a $0 that read as nothing more to come.
 */
export function scheduledRevenueRow(
  job: Job,
  scheduled: number,
): { caption: string; value: string } {
  const billingMode = job.recurringConfig?.billingMode ?? 'PER_VISIT';
  if (billingMode === 'SUBSCRIPTION') {
    const fee = billingHeadline(job).amount;
    if (!job.endDate && fee != null) {
      return {
        caption: 'billed every month, no end date',
        value: `${money(fee, !Number.isInteger(fee))}/mo`,
      };
    }
    return { caption: 'the months still to bill', value: money(scheduled) };
  }
  if (billingMode === 'PREPAID') {
    return scheduled > 0
      ? { caption: 'the term still to bill', value: money(scheduled) }
      : { caption: 'billed up front', value: money(0) };
  }
  return { caption: 'the visits still to bill', value: money(scheduled) };
}

/**
 * A per-visit series invoices each visit on the server, a few seconds after it
 * completes, and a prepaid series does the same for a visit's extras (the term
 * itself is billed up front) — so for a moment a visit completed on this page
 * has no invoice. True while some such visit completed in the last two minutes
 * and no invoice covers it yet: the page reads its invoices again until one
 * does.
 */
export function awaitsVisitInvoice(
  job: Pick<Job, 'recurringConfig' | 'invoicingSchedule'>,
  appointments: readonly JobAppointment[],
  invoices: readonly Pick<Invoice, 'appointmentIds'>[],
  now: number,
): boolean {
  const billingMode = job.recurringConfig?.billingMode ?? 'PER_VISIT';
  // A subscription bills its visits' extras with the next period.
  if (billingMode === 'SUBSCRIPTION') return false;
  if (
    billingMode === 'PER_VISIT' &&
    job.invoicingSchedule &&
    job.invoicingSchedule !== 'ON_COMPLETION'
  ) {
    return false;
  }
  const invoiced = new Set(invoices.flatMap((invoice) => invoice.appointmentIds ?? []));
  return appointments.some((visit) => {
    if (visit.status !== 'COMPLETED' || !visit.completedAt || invoiced.has(visit.id)) {
      return false;
    }
    if (billingMode === 'PREPAID' && !visit.additionalItems?.length) return false;
    return now - Date.parse(visit.completedAt) < VISIT_INVOICE_WAIT_MS;
  });
}

/**
 * Billing-mode-aware price to DISPLAY for a job across the list / header / visit
 * surfaces. A recurring SUBSCRIPTION/PREPAID job bills a period/term amount
 * (PricingTerms.baseAmount) that is decoupled from the line-item scope, so those
 * surfaces must show the billing headline (`$100 /mo`), NOT `grandTotal` (the scope).
 * One-off and per-visit recurring keep showing the scope grandTotal (correct there).
 */
export function jobPriceDisplay(job: Job): {
  amount: number;
  unit: string;
  subtitle: string;
  isRecurringBilled: boolean;
} {
  const mode = job.recurringConfig?.billingMode ?? null;
  if (job.bookingType === 'RECURRING' && (mode === 'SUBSCRIPTION' || mode === 'PREPAID')) {
    const headline = billingHeadline(job);
    return {
      amount: headline.amount ?? 0,
      unit: headline.unit,
      subtitle: headline.caption,
      isRecurringBilled: true,
    };
  }
  return {
    amount: job.grandTotal ?? 0,
    unit: '',
    // NOT-540 — the pre-tax subtotal is a real money figure, so show it to the
    // cent. The whole-dollar `money()` default is for headline amounts (mockup
    // `$185`); rounding it here misrepresented the sum (e.g. $555.55 → "$556 + tax").
    subtitle: `${money(job.subtotal, true)} + tax`,
    isRecurringBilled: false,
  };
}

/** Billing model and when it bills (e.g. "Per-visit", "on completion"). */
export function billingModelParts({ billingMode, invoicingSchedule }: BillingModeInput): {
  model: string;
  schedule: string | null;
} {
  const model =
    billingMode === 'SUBSCRIPTION'
      ? 'Subscription'
      : billingMode === 'PREPAID'
        ? 'Prepaid'
        : 'Per-visit';
  const scheduleLabel: Record<string, string> = {
    ON_COMPLETION: 'on completion',
    LAST_DAY_OF_MONTH: 'last day of month',
    CUSTOM_SCHEDULE: 'custom schedule',
    AS_NEEDED: 'as needed',
  };
  const schedule = invoicingSchedule
    ? scheduleLabel[invoicingSchedule]
    : billingMode === 'SUBSCRIPTION'
      ? 'monthly'
      : billingMode === 'PREPAID'
        ? 'term'
        : null;
  return { model, schedule };
}

/** Billing-model + schedule summary core (e.g. "Per-visit · on completion"). */
export function billingModelSummaryFrom(input: BillingModeInput): string {
  const { model, schedule } = billingModelParts(input);
  return schedule ? `${model} · ${schedule}` : model;
}

/** Billing-model + schedule summary pill (e.g. "Per-visit · on completion"). */
export function billingModelSummary(job: Job): string {
  const cfg = job.recurringConfig ?? null;
  return billingModelSummaryFrom({
    billingMode: cfg?.billingMode ?? null,
    invoicingSchedule: job.invoicingSchedule ?? null,
  });
}

/** 4-step lifecycle stepper index; recovery/manual holds map to the Active step. */
export const PLAN_STEPS = ['Draft', 'Active', 'Renewing', 'Ended'] as const;
export function currentStepIndex(status: RecurringStatus): number {
  switch (status) {
    case 'DRAFT':
      return 0;
    case 'ACTIVE':
    case 'PAST_DUE':
    case 'SUSPENDED':
    case 'PAUSED':
      return 1;
    case 'ENDED':
      return 3;
    default:
      return 0;
  }
}

/**
 * Done / remaining visit counts from the materialised child appointments.
 * Done = completed; remaining = scheduled/future (not skipped/cancelled).
 */
export function visitCounts(appointments: JobAppointment[]): {
  done: number;
  remaining: number;
  total: number;
} {
  let done = 0;
  let remaining = 0;
  for (const a of appointments) {
    if (a.status === 'COMPLETED') done += 1;
    else if (!isSkippedVisit(a)) remaining += 1;
  }
  return { done, remaining, total: appointments.length };
}

/**
 * The next upcoming (scheduled) visit, earliest first. A day-bound DAY_FLEXIBLE
 * visit awaiting the route optimizer has no startAt but IS scheduled (to a day),
 * so `startAt ?? scheduledDate` is the sort/eligibility anchor — otherwise the
 * hero card would show "No visit scheduled" for a fully materialized route day.
 * (Lexicographic compare is safe: both are ISO-ordered, and a date-only day
 * sorts before any instant on that same day.) A visit still waiting for its
 * day — a period of a series without a fixed day — is next only when no open
 * visit has a date yet, earliest-numbered first.
 */
export function nextUpcomingVisit(appointments: JobAppointment[]): JobAppointment | null {
  const anchor = (a: JobAppointment) => a.startAt ?? a.scheduledDate ?? null;
  const open = appointments.filter((a) => !isSkippedVisit(a) && a.status !== 'COMPLETED');
  const upcoming = open
    .filter((a) => anchor(a) != null)
    .sort((x, y) => (anchor(x)! < anchor(y)! ? -1 : 1));
  const waiting = open
    .filter((a) => anchor(a) == null)
    .sort(
      (x, y) =>
        (x.appointmentNumber ?? Number.MAX_SAFE_INTEGER) -
        (y.appointmentNumber ?? Number.MAX_SAFE_INTEGER),
    );
  return upcoming[0] ?? waiting[0] ?? null;
}

/** Visit label like `-07` from the appointment number. */
export function visitTag(a: JobAppointment): string {
  return a.appointmentNumber != null ? `‑${String(a.appointmentNumber).padStart(2, '0')}` : '—';
}

/**
 * GAP-01 — a visit whose crew can still be (re)assigned. Only upcoming, not-yet-
 * worked states qualify; COMPLETED/SKIPPED/CANCELLED/IN_PROGRESS/etc. do not.
 */
/**
 * A series whose generator still creates visits — the hourly top-up runs only
 * these — so the days it projects are days it will visit.
 */
export function seriesCreatesVisits(status: RecurringStatus | null | undefined): boolean {
  return status === 'ACTIVE' || status === 'PAST_DUE';
}

const ASSIGNABLE_VISIT_STATUSES: ReadonlySet<JobAppointmentStatus> = new Set([
  'RESERVED',
  'SCHEDULED',
  'CONFIRMED',
  'DISPATCHED',
]);

export function isAssignableVisit(a: JobAppointment): boolean {
  return ASSIGNABLE_VISIT_STATUSES.has(a.status);
}
